import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Drawer,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select as MuiSelect,
    TextField,
    Typography,
} from '@mui/material';
import React, {
    UIEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { clientsAPI } from '../../../../api/clientsAPI';
import useUserProperties, {
    UserPropertyDefinition,
} from '../../../../hooks/useUserProperties';
import {
    createBookingReference,
    deleteBookingReference,
    getBookingReferences,
    updateBookingReference,
} from '../../../../reducers/clientsReducer';
import BasicTable from '../../../Common/Tables/BasicTable/BasicTable';
import styles from './bookingReferencesStyle';
const allReferencesTypes = [
  {
    label: 'Free Format',
    value: 'FreeFormat',
  },
  {
    label: 'List',
    value: 'Array',
  },
]
export type BookingReferencesProps = {
  client: { id: number; [key: string]: any }
}
export type bookingReferenceArrayType = {
  id: string
  value: string
}
export type bookingReferenceType = {
  bookingReferenceId: number
  clientId: number
  referenceType: string
  title: string
  values: Array<bookingReferenceArrayType>
  valuesCount?: number
}

const BOOKING_REFERENCE_FLYOUT_PAGE_SIZE = 50
const BOOKING_REFERENCE_PREVIEW_PAGE_SIZE = 3

const getPagedBookingReferenceValues = (payload: any) => {
  const seen = new Set<any>()

  const extract = (node: any, depth = 0): any[] => {
    if (!node || depth > 6) return []
    if (Array.isArray(node)) return node
    if (typeof node !== 'object') return []
    if (seen.has(node)) return []
    seen.add(node)

    if (Array.isArray(node.items)) return node.items
    if (Array.isArray(node.value)) return node.value
    if (Array.isArray(node.values)) return node.values

    const nestedKeys = ['data', 'value', 'result', 'results', 'payload']
    for (const key of nestedKeys) {
      const nested = node[key]
      const extracted = extract(nested, depth + 1)
      if (extracted.length > 0) return extracted
    }

    return []
  }

  return extract(payload)
}

const getBookingReferenceValueText = (value: any): string | null => {
  if (value === null || value === undefined) return null

  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim()
    return text ? text : null
  }

  if (typeof value !== 'object') {
    const text = String(value).trim()
    return text ? text : null
  }

  const candidate =
    value?.value ??
    value?.label ??
    value?.name ??
    value?.displayValue ??
    value?.code ??
    value?.key ??
    value?.id

  if (candidate === null || candidate === undefined) return null

  const text = String(candidate).trim()
  return text ? text : null
}

const getPagedBookingReferenceTotalCount = (
  payload: any,
  fallbackCount: number,
): number => {
  const seen = new Set<any>()

  const extract = (node: any, depth = 0): number | null => {
    if (!node || depth > 6) return null
    if (typeof node !== 'object') return null
    if (seen.has(node)) return null
    seen.add(node)

    const directCandidates = [
      node?.totalSize,
      node?.totalCount,
      node?.total,
      node?.count,
      node?.pagination?.totalSize,
      node?.pagination?.totalCount,
      node?.pagination?.total,
      node?.meta?.totalSize,
      node?.meta?.totalCount,
    ]

    for (const candidate of directCandidates) {
      if (typeof candidate === 'number') return candidate
    }

    const nestedKeys = ['data', 'value', 'result', 'results', 'payload']
    for (const key of nestedKeys) {
      const nestedTotal = extract(node[key], depth + 1)
      if (typeof nestedTotal === 'number') return nestedTotal
    }

    return null
  }

  const total = extract(payload)
  return typeof total === 'number' ? total : fallbackCount
}

const getPagedBookingReferenceHasMore = (
  payload: any,
  page: number,
  limit: number,
  resultCount: number,
) => {
  const explicitHasMore =
    payload?.hasMore ??
    payload?.pagination?.hasMore ??
    payload?.meta?.hasMore ??
    payload?.pageInfo?.hasMore

  if (typeof explicitHasMore === 'boolean') {
    return explicitHasMore
  }

  const total =
    payload?.totalSize ??
    payload?.totalCount ??
    payload?.total ??
    payload?.count ??
    payload?.pagination?.totalSize ??
    payload?.pagination?.totalCount ??
    payload?.pagination?.total ??
    payload?.meta?.totalSize ??
    payload?.meta?.totalCount

  if (typeof total === 'number') {
    return page * limit < total
  }

  return resultCount >= limit
}

type BookingReferenceOption = {
  label: string
  value: string
}

type BookingReferenceValuesCellProps = {
  bookingReferenceId: number
  referenceType: string
  currentValues: any[]
  currentValueCount?: number
  clientId: number
  onUpdateBookingReference: (
    bookingReferenceId: number,
    bookingReference: any,
  ) => void
  normalizeString: (value: string) => string
}

const BookingReferenceValuesCell = ({
  bookingReferenceId,
  referenceType,
  currentValues,
  currentValueCount,
  clientId,
  onUpdateBookingReference,
  normalizeString,
}: BookingReferenceValuesCellProps) => {
  const isDisabled = referenceType === 'FreeFormat'
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [options, setOptions] = useState<BookingReferenceOption[]>([])
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const [newValueText, setNewValueText] = useState('')
  const requestIdRef = useRef(0)
  const hasMoreRef = useRef(false)
  const isLoadingRef = useRef(false)
  const hasTouchedSelectionRef = useRef(false)

  const normalizedCurrentValues = useMemo(() => {
    const sourceValues = Array.isArray(currentValues)
      ? currentValues
      : getPagedBookingReferenceValues(currentValues)

    return (sourceValues || [])
      .map((item: any) => getBookingReferenceValueText(item))
      .filter(Boolean)
      .map((value) => normalizeString(value as string))
  }, [currentValues, normalizeString])
  const displayValueCount =
    typeof currentValueCount === 'number'
      ? currentValueCount
      : normalizedCurrentValues.length
  const previewValues = useMemo(
    () => normalizedCurrentValues.slice(0, 3),
    [normalizedCurrentValues],
  )
  const remainingCount = Math.max(displayValueCount - previewValues.length, 0)

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, 250)

    return () => clearTimeout(debounceTimeout)
  }, [searchText])

  useEffect(() => {
    if (!drawerOpen) return
    hasTouchedSelectionRef.current = false
    setSelectedValues(normalizedCurrentValues)
  }, [drawerOpen, normalizedCurrentValues])

  const loadValuesPage = useCallback(
    async (nextPage: number, mode: 'replace' | 'append') => {
      if (isLoadingRef.current) return
      if (mode === 'append' && !hasMoreRef.current) return

      const requestId = ++requestIdRef.current
      setIsLoading(true)

      if (mode === 'replace') {
        setOptions([])
        setPage(0)
        setHasMore(false)
      }

      try {
        const trimmedSearch = debouncedSearchText.trim()
        const offset = (nextPage - 1) * BOOKING_REFERENCE_FLYOUT_PAGE_SIZE

        const response = await clientsAPI.getBookingReferenceValues(
          clientId,
          bookingReferenceId,
          {
            limit: BOOKING_REFERENCE_FLYOUT_PAGE_SIZE,
            offset,
            ...(trimmedSearch ? { search: trimmedSearch } : {}),
          },
        )

        if (requestId !== requestIdRef.current) return

        const values = getPagedBookingReferenceValues(response?.data)
        const nextOptions = values
          .map((val: any) => {
            const stringVal = getBookingReferenceValueText(val)
            if (!stringVal) return null
            const normalizedVal = normalizeString(stringVal)
            return {
              label: normalizedVal,
              value: normalizedVal,
            }
          })
          .filter(Boolean) as BookingReferenceOption[]

        setOptions((previous) =>
          mode === 'replace' ? nextOptions : [...previous, ...nextOptions],
        )
        if (!hasTouchedSelectionRef.current) {
          setSelectedValues((previous) => {
            const merged = new Set(previous)
            nextOptions.forEach((option) => merged.add(option.value))
            return Array.from(merged)
          })
        }
        setPage(nextPage)
        setHasMore(
          getPagedBookingReferenceHasMore(
            response?.data,
            nextPage,
            BOOKING_REFERENCE_FLYOUT_PAGE_SIZE,
            values.length,
          ),
        )
      } catch (error) {
        if (requestId !== requestIdRef.current) return
        setHasMore(false)
        if (mode === 'replace') {
          setOptions([])
          setPage(0)
        }
        console.error('Failed to load booking reference values', error)
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    },
    [bookingReferenceId, clientId, debouncedSearchText, normalizeString],
  )

  useEffect(() => {
    if (!drawerOpen) return
    void loadValuesPage(1, 'replace')
  }, [debouncedSearchText, drawerOpen, loadValuesPage])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (isLoadingRef.current || !hasMoreRef.current) return

      const target = event.currentTarget
      const distanceFromBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight

      if (distanceFromBottom <= 16) {
        void loadValuesPage(page + 1, 'append')
      }
    },
    [loadValuesPage, page],
  )

  const toggleValue = (value: string) => {
    hasTouchedSelectionRef.current = true
    setSelectedValues((previous) =>
      previous.includes(value)
        ? previous.filter((item) => item !== value)
        : [...previous, value],
    )
  }

  const handleAddValue = () => {
    const trimmed = normalizeString(newValueText.trim())
    if (!trimmed) return

    hasTouchedSelectionRef.current = true
    setSelectedValues((previous) =>
      previous.includes(trimmed) ? previous : [...previous, trimmed],
    )
    setOptions((previous) => {
      if (previous.some((item) => item.value === trimmed)) return previous
      return [{ label: trimmed, value: trimmed }, ...previous]
    })
    setNewValueText('')
  }

  if (isDisabled) {
    return (
      <Typography variant="body2" color="text.secondary">
        ---
      </Typography>
    )
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              mt: 0.25,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
            }}
            title={normalizedCurrentValues.join(', ')}
          >
            {previewValues.length === 0 && displayValueCount === 0 && (
              <Typography
                variant="body2"
                sx={{ color: '#1f1f1f', lineHeight: 1.3, padding: 0 }}
              >
                No values
              </Typography>
            )}
            {previewValues.map((value, index) => (
              <Typography
                key={`${value}_${index}`}
                variant="body2"
                sx={{
                  color: '#1f1f1f',
                  lineHeight: 1.3,
                  wordBreak: 'break-word',
                }}
              >
                {value}
              </Typography>
            ))}
            {remainingCount > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.3 }}
              >
                + {remainingCount} more
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setDrawerOpen(true)}
        >
          Edit
        </Button>
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setSearchText('')
          setDebouncedSearchText('')
          setOptions([])
          setPage(0)
          setHasMore(false)
          setIsLoading(false)
          requestIdRef.current += 1
          hasTouchedSelectionRef.current = false
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#F8F8F8',
            color: '#8E8E8E',
            fontSize: '14px',
          },
        }}
      >
        <Box
          sx={{
            width: { xs: '100vw', sm: 520 },
            display: 'flex',
            justifyContent: 'center',
            pb: 3,
          }}
        >
          <Box sx={{ width: '90%', pt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: '#1f1f1f', fontWeight: 600 }}
              >
                Edit values
              </Typography>
              <Button
                size="small"
                onClick={() => {
                  setDrawerOpen(false)
                  hasTouchedSelectionRef.current = false
                }}
              >
                Close
              </Button>
            </Box>
            <Typography sx={{ mb: 2 }}>
              Add, remove, or search reference values.
            </Typography>
            <TextField
              size="small"
              fullWidth
              label="Search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              sx={{ backgroundColor: '#ffffff' }}
            />
            <Box sx={{ mt: 2, p: 0, display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                sx={{ m: 0 }}
                label="Add value"
                value={newValueText}
                onChange={(event) => setNewValueText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handleAddValue()
                  }
                }}
              />
              <Button variant="outlined" sx={{ m: 0 }} onClick={handleAddValue}>
                Add
              </Button>
            </Box>
            <Box
              sx={{
                mt: 2,
                border: '1px solid',
                borderColor: '#e0e0e0',
                borderRadius: 1,
                maxHeight: '60vh',
                overflowY: 'auto',
                backgroundColor: '#ffffff',
              }}
              onScroll={handleScroll}
            >
              <List dense disablePadding>
                {options.map((option) => (
                  <ListItemButton
                    key={option.value}
                    onClick={() => toggleValue(option.value)}
                  >
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selectedValues.includes(option.value)}
                    />
                    <ListItemText primary={option.label} />
                  </ListItemButton>
                ))}
              </List>
              {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {!isLoading && options.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No results
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                mt: 2,
                gap: 1,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setDrawerOpen(false)
                  setSelectedValues(normalizedCurrentValues)
                  hasTouchedSelectionRef.current = false
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  onUpdateBookingReference(bookingReferenceId, {
                    values: selectedValues,
                  })
                  setDrawerOpen(false)
                  hasTouchedSelectionRef.current = false
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
type BookingReferenceFormState = {
  isMandatory: boolean
  regex: string
  defaultValueType: '' | 'Specified' | 'UserProperty'
  defaultValue: string
  userPropertyId: string
}

const EMPTY_FORM_STATE: BookingReferenceFormState = {
  isMandatory: false,
  regex: '',
  defaultValueType: '',
  defaultValue: '',
  userPropertyId: '',
}

const buildDefaultValuePayload = (form: BookingReferenceFormState) => {
  const patch: any = {
    isMandatory: form.isMandatory,
    regex: form.regex || '',
  }
  if (form.defaultValueType === 'Specified') {
    patch.defaultValueType = 'Specified'
    patch.defaultValue = form.defaultValue
    patch.userPropertyId = null
  } else if (form.defaultValueType === 'UserProperty') {
    patch.defaultValueType = 'UserProperty'
    patch.userPropertyId =
      form.userPropertyId === '' ? null : Number(form.userPropertyId)
    patch.defaultValue = null
  } else {
    patch.defaultValueType = null
    patch.defaultValue = null
    patch.userPropertyId = null
  }
  return patch
}

const BookingReferenceFormFields = ({
  form,
  setForm,
  userProperties,
  hideDefaultValueSource = false,
}: {
  form: BookingReferenceFormState
  setForm: React.Dispatch<React.SetStateAction<BookingReferenceFormState>>
  userProperties: UserPropertyDefinition[]
  hideDefaultValueSource?: boolean
}) => (
  <>
    <FormControlLabel
      sx={{ mt: 1, display: 'block' }}
      control={
        <Checkbox
          size="small"
          checked={form.isMandatory}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, isMandatory: event.target.checked }))
          }
        />
      }
      label="Mandatory"
    />
    <TextField
      fullWidth
      size="small"
      sx={{ mt: 1 }}
      label="Validation regex (optional)"
      value={form.regex}
      onChange={(event) =>
        setForm((prev) => ({ ...prev, regex: event.target.value }))
      }
    />
    {!hideDefaultValueSource && (
      <FormControl fullWidth size="small" sx={{ mt: 2 }}>
        <InputLabel id="default-value-type-label">
          Pre-fill this field with
        </InputLabel>
        <MuiSelect
          labelId="default-value-type-label"
          label="Pre-fill this field with"
          value={form.defaultValueType}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              defaultValueType: event.target
                .value as BookingReferenceFormState['defaultValueType'],
            }))
          }
        >
          <MenuItem value="">Nothing</MenuItem>
          <MenuItem value="Specified">A fixed value</MenuItem>
          <MenuItem value="UserProperty">
            A value from the traveller&apos;s profile
          </MenuItem>
        </MuiSelect>
      </FormControl>
    )}
    {!hideDefaultValueSource && form.defaultValueType === 'Specified' && (
      <TextField
        fullWidth
        size="small"
        sx={{ mt: 1 }}
        label="Default value"
        value={form.defaultValue}
        onChange={(event) =>
          setForm((prev) => ({ ...prev, defaultValue: event.target.value }))
        }
      />
    )}
    {!hideDefaultValueSource && form.defaultValueType === 'UserProperty' && (
      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
        <InputLabel id="user-property-id-label">Traveller property</InputLabel>
        <MuiSelect
          labelId="user-property-id-label"
          label="Traveller property"
          value={form.userPropertyId}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              userPropertyId: String(event.target.value),
            }))
          }
        >
          {userProperties.length === 0 && (
            <MenuItem value="" disabled>
              No traveller properties available
            </MenuItem>
          )}
          {userProperties.map((prop) => (
            <MenuItem key={prop.id} value={String(prop.id)}>
              {prop.title}
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
    )}
  </>
)

type BookingReferenceDrawerProps = {
  open: boolean
  mode: 'create' | 'edit'
  bookingReference: any | null
  userProperties: UserPropertyDefinition[]
  onClose: () => void
  onSubmit: (payload: any, bookingReferenceId?: number) => void
}

const BookingReferenceDrawer = ({
  open,
  mode,
  bookingReference,
  userProperties,
  onClose,
  onSubmit,
}: BookingReferenceDrawerProps) => {
  const [title, setTitle] = useState('')
  const [referenceType, setReferenceType] = useState<'FreeFormat' | 'Array'>(
    'FreeFormat',
  )
  const [status, setStatus] = useState<'Active' | 'Deleted'>('Active')
  const [values, setValues] = useState<string[]>([])
  const [form, setForm] = useState<BookingReferenceFormState>(EMPTY_FORM_STATE)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && bookingReference) {
      setTitle(bookingReference.title || '')
      setReferenceType(
        (bookingReference.referenceType as 'FreeFormat' | 'Array') ||
          'FreeFormat',
      )
      setStatus((bookingReference.status as 'Active' | 'Deleted') || 'Active')
      setValues([])
      setForm({
        isMandatory: Boolean(bookingReference.isMandatory),
        regex: bookingReference.regex || '',
        defaultValueType:
          (bookingReference.defaultValueType as
            | ''
            | 'Specified'
            | 'UserProperty') || '',
        defaultValue: bookingReference.defaultValue || '',
        userPropertyId:
          typeof bookingReference.userPropertyId === 'number'
            ? String(bookingReference.userPropertyId)
            : '',
      })
    } else {
      setTitle('')
      setReferenceType('FreeFormat')
      setStatus('Active')
      setValues([])
      setForm(EMPTY_FORM_STATE)
    }
  }, [open, mode, bookingReference])

  const handleSave = () => {
    if (mode === 'create') {
      // Server-side AutoMapper currently can't map DefaultValueType on create,
      // so omit default-value fields here. Users set defaults via the edit
      // drawer after creation.
      const createPayload: any = {
        title,
        referenceType,
        isMandatory: form.isMandatory,
      }
      if (form.regex) createPayload.regex = form.regex
      if (referenceType === 'Array') {
        createPayload.values = values
      }
      onSubmit(createPayload)
      return
    }
    const payload: any = {
      title,
      referenceType,
      status,
      ...buildDefaultValuePayload(form),
    }
    // Switching a typed-list to free format - clear the now-irrelevant values.
    if (
      bookingReference?.referenceType !== 'FreeFormat' &&
      referenceType === 'FreeFormat'
    ) {
      payload.values = []
    }
    onSubmit(payload, bookingReference?.bookingReferenceId)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: '#F8F8F8',
          color: '#8E8E8E',
          fontSize: '14px',
        },
      }}
    >
      <Box
        sx={{
          width: { xs: '100vw', sm: 520 },
          display: 'flex',
          justifyContent: 'center',
          pb: 3,
        }}
      >
        <Box sx={{ width: '90%', pt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#1f1f1f', fontWeight: 600 }}>
              {mode === 'create'
                ? 'Create booking reference'
                : 'Edit booking reference'}
            </Typography>
            <Button size="small" onClick={onClose}>
              Close
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1, backgroundColor: '#ffffff' }}
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="reference-type-label">Reference type</InputLabel>
            <MuiSelect
              labelId="reference-type-label"
              label="Reference type"
              value={referenceType}
              onChange={(event) =>
                setReferenceType(event.target.value as 'FreeFormat' | 'Array')
              }
            >
              <MenuItem value="FreeFormat">Free format</MenuItem>
              <MenuItem value="Array">List</MenuItem>
            </MuiSelect>
          </FormControl>
          {mode === 'create' && referenceType === 'Array' && (
            <Box sx={{ mt: 2 }}>
              <CreatableSelect
                isMulti
                placeholder="Add reference options"
                value={values.map((v) => ({ label: v, value: v }))}
                onChange={(newValue) => {
                  setValues(
                    (newValue ?? []).map((item: any) => String(item.value)),
                  )
                }}
              />
            </Box>
          )}
          <BookingReferenceFormFields
            form={form}
            setForm={setForm}
            userProperties={userProperties}
            hideDefaultValueSource={mode === 'create'}
          />
          {mode === 'edit' && (
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel id="status-label">Status</InputLabel>
              <MuiSelect
                labelId="status-label"
                label="Status"
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as 'Active' | 'Deleted')
                }
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Deleted">Deleted</MenuItem>
              </MuiSelect>
            </FormControl>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3,
              gap: 1,
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  )
}

const BookingReferences: React.SFC<BookingReferencesProps> = (props) => {
  const dispatch = useDispatch()
  const { userProperties } = useUserProperties(props.client.id)
  const [allBookingReferences, setAllBookingReferences] = React.useState<any>(
    [],
  )
  // Load booking reference values with pagination and search
  const normalizeString = (str: string) => {
    return str.replace(/–|-/g, '-')
  }
  const [drawerMode, setDrawerMode] = React.useState<'create' | 'edit' | null>(
    null,
  )
  const [editingRef, setEditingRef] = React.useState<any | null>(null)
  const getBookingReferencesForClient = async (clientId: any) => {
    const result: any = await dispatch(getBookingReferences(clientId))
    if (!result.error) {
      // Load values for Array type references
      const referencesWithValues = await Promise.all(
        result.payload.map(async (ref: any) => {
          if (ref.referenceType === 'Array') {
            try {
              const valuesResponse = await clientsAPI.getBookingReferenceValues(
                clientId,
                ref.bookingReferenceId,
                { limit: BOOKING_REFERENCE_PREVIEW_PAGE_SIZE, offset: 0 },
              )
              const rawValues = getPagedBookingReferenceValues(
                valuesResponse?.data,
              )
              const normalizedValues = rawValues
                .map((val: any) => {
                  const stringVal = getBookingReferenceValueText(val)
                  if (!stringVal) return null
                  return normalizeString(stringVal)
                })
                .filter(Boolean) as string[]
              const valuesCount = getPagedBookingReferenceTotalCount(
                valuesResponse?.data,
                normalizedValues.length,
              )
              return {
                ...ref,
                values: normalizedValues,
                valuesCount,
              }
            } catch (error) {
              console.error(
                'Failed to load values for ref',
                ref.bookingReferenceId,
                error,
              )
              const fallbackValues = (
                Array.isArray(ref.values) ? ref.values : []
              )
                .map((val: any) => getBookingReferenceValueText(val))
                .filter(Boolean)
                .map((value: any) => normalizeString(value))
              return {
                ...ref,
                values: fallbackValues.slice(
                  0,
                  BOOKING_REFERENCE_PREVIEW_PAGE_SIZE,
                ),
                valuesCount: fallbackValues.length,
              }
            }
          }
          return ref
        }),
      )
      setAllBookingReferences(referencesWithValues)
    }
  }
  useEffect(() => {
    getBookingReferencesForClient(props.client.id)
  }, [])

  const onDeleteBookingReference = (bookingReferenceId: any) => {
    dispatch(
      deleteBookingReference({
        clientId: props.client.id,
        bookingReferenceId: bookingReferenceId,
      }),
    )
      // @ts-ignore - Redux thunk dispatch returns a promise
      .then((result: any) => {
        if (!result.error) {
          getBookingReferencesForClient(props.client.id)
        }
      })
  }
  const onUpdateBookingReference = (
    bookingReferenceId: any,
    bookingReference: any,
  ) => {
    dispatch(
      updateBookingReference({
        clientId: props.client.id,
        bookingReferenceId: bookingReferenceId,
        bookingReference: { ...bookingReference },
      }),
    )
      // @ts-ignore - Redux thunk dispatch returns a promise
      .then((result: any) => {
        if (!result.error) {
          getBookingReferencesForClient(props.client.id)
        }
      })
  }
  const onUpdateReferenceType = (
    bookingReferenceId: any,
    referenceType: any,
  ) => {
    var bookingReference = {
      referenceType: referenceType,
      values: referenceType === 'FreeFormat' ? [] : null,
    }
    onUpdateBookingReference(bookingReferenceId, bookingReference)
  }
  const openCreateDrawer = () => {
    setEditingRef(null)
    setDrawerMode('create')
  }
  const openEditDrawer = (bookingReference: any) => {
    setEditingRef(bookingReference)
    setDrawerMode('edit')
  }
  const closeDrawer = () => {
    setDrawerMode(null)
    setEditingRef(null)
  }
  const handleDrawerSubmit = (payload: any, bookingReferenceId?: number) => {
    if (drawerMode === 'edit' && bookingReferenceId) {
      onUpdateBookingReference(bookingReferenceId, payload)
      closeDrawer()
      return
    }
    dispatch(
      createBookingReference({
        clientId: props.client.id,
        bookingReference: payload,
      }),
    )
      // @ts-ignore - Redux thunk dispatch returns a promise
      .then((result: any) => {
        if (!result.error) {
          closeDrawer()
          getBookingReferencesForClient(props.client.id)
        }
      })
  }
  const columns = React.useMemo(
    () => [
      {
        Header: 'Booking References',
        columns: [
          {
            Header: 'Title',
            accessor: 'title',
          },
          {
            Header: 'Reference Type',
            accessor: 'referenceType',
            Cell: (props: any) => (
              <Select
                onChange={(type: any) =>
                  onUpdateReferenceType(
                    props.row.original.bookingReferenceId,
                    type.value,
                  )
                }
                options={allReferencesTypes}
                defaultValue={allReferencesTypes.find(
                  (referenceType) => referenceType.value === props.cell.value,
                )}
              />
            ),
          },
          {
            Header: 'Values',
            accessor: 'values',
            Cell: (cellProps: any) => {
              const bookingReferenceId =
                cellProps.row.original.bookingReferenceId
              const currentValues =
                cellProps.row.original.values ?? cellProps.cell.value ?? []
              return (
                <BookingReferenceValuesCell
                  bookingReferenceId={bookingReferenceId}
                  referenceType={cellProps.cell.row.original.referenceType}
                  currentValues={currentValues}
                  currentValueCount={cellProps.row.original.valuesCount}
                  clientId={props.client.id}
                  onUpdateBookingReference={onUpdateBookingReference}
                  normalizeString={normalizeString}
                />
              )
            },
          },
          {
            Header: 'Actions',
            accessor: 'actions',
            sortable: false,
            filterable: false,
            Cell: (cellProps: any) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  color="primary"
                  onClick={() => openEditDrawer(cellProps.row.original)}
                >
                  <EditIcon />
                </Button>
                <Button
                  color="error"
                  onClick={() =>
                    onDeleteBookingReference(
                      cellProps.row.original.bookingReferenceId,
                    )
                  }
                >
                  <DeleteIcon />
                </Button>
              </Box>
            ),
          },
        ],
      },
    ],
    [allBookingReferences, onUpdateReferenceType, onDeleteBookingReference],
  )
  return (
    <Box component={'div'} sx={styles.root}>
      <Grid container>
        <Grid item xs={6}>
          <Box component={'div'} sx={styles.leftHeading}>
            <FormLabel>Booking References</FormLabel>
          </Box>
          <Button
            color="primary"
            onClick={openCreateDrawer}
            variant="outlined"
            size="small"
          >
            Create
          </Button>
        </Grid>
        <Grid item xs={12} sx={styles.table}>
          <BasicTable
            columns={columns}
            data={allBookingReferences}
            hideColumns={[]}
          />
        </Grid>
      </Grid>
      <BookingReferenceDrawer
        open={drawerMode !== null}
        mode={drawerMode ?? 'create'}
        bookingReference={editingRef}
        userProperties={userProperties}
        onClose={closeDrawer}
        onSubmit={handleDrawerSubmit}
      />
    </Box>
  )
}
export default BookingReferences
